<!--
  A block of text that exists to be copied: a URL, a shell command, a JSON
  config, a prompt.

  Two things it is careful about.

  The clipboard is not always available -- an insecure origin, a denied
  permission, an embedded webview -- and a Copy button that silently does
  nothing is worse than no button. So the failure is said out loud, in the
  button and under it, and the text stays selectable so the person can do it by
  hand.

  The button sits INSIDE the block rather than beside it. A row layout puts the
  button's width in competition with the code's, and these blocks carry a
  wrapped shell command; overlaying it and padding the code out from under it
  keeps the code's measure the same whatever the button says.
-->
<script lang="ts">
  import { onDestroy } from 'svelte'

  let {
    text,
    label = '',
    hint = '',
  }: {
    /** The exact characters the button puts on the clipboard. */
    text: string
    /** A small uppercase-mono caption above the block. */
    label?: string
    /** One line under the block, for a caveat the code cannot carry itself. */
    hint?: string
  } = $props()

  let phase = $state<'idle' | 'copied' | 'failed'>('idle')
  let timer: ReturnType<typeof setTimeout> | undefined

  onDestroy(() => clearTimeout(timer))

  async function copy() {
    clearTimeout(timer)
    try {
      await navigator.clipboard.writeText(text)
      phase = 'copied'
    } catch {
      phase = 'failed'
    }
    timer = setTimeout(() => (phase = 'idle'), 2500)
  }
</script>

<div class="copybox">
  {#if label}<span class="copybox-label meta-label">{label}</span>{/if}
  <div class="copybox-body">
    <pre><code>{text}</code></pre>
    <button type="button" class="btn btn-quiet btn-small copybox-btn" onclick={copy}>
      {phase === 'copied' ? 'Copied' : phase === 'failed' ? "Can't copy" : 'Copy'}
    </button>
  </div>
  {#if phase === 'failed'}
    <p class="copybox-note" role="alert">
      This browser would not give up the clipboard — select the text and copy it by hand.
    </p>
  {:else if hint}
    <p class="copybox-note">{hint}</p>
  {/if}
</div>

<style>
  .copybox {
    margin: 0 0 1.5rem;
  }

  .copybox-label {
    display: block;
    color: var(--color-ink-soft);
    margin-bottom: 0.45rem;
  }

  .copybox-body {
    position: relative;
  }

  pre {
    margin: 0;
    background: var(--color-plinth);
    border: 1px solid var(--color-line);
    border-radius: 6px;
    /* Right padding clears the overlaid button on the first line. */
    padding: 0.85rem 6rem 0.85rem 0.9rem;
    overflow: hidden;
  }

  code {
    font-family: var(--font-mono);
    font-size: 0.8125rem;
    line-height: 1.65;
    color: var(--ink);
    /* Wrap rather than scroll: a horizontal scrollbar inside a page people
       read top to bottom hides the end of exactly the line they need. */
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    tab-size: 2;
  }

  .copybox-btn {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }

  .copybox-note {
    margin: 0.45rem 0 0;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: var(--color-ink-soft);
  }
</style>
